/**
 * customLegends — provider-declared legends for layers whose symbology is too
 * complex to auto-derive (the "custom legend" tier; simple layers stay on the
 * auto path in legendModel).
 *
 * Each builder is sourced from the SAME symbology constants/functions the map
 * uses to render, so the legend can never drift from what is actually drawn.
 * A layer resolves to a custom legend by its config.providerId (live providers)
 * or by its layer type (e.g. VAR). Register a builder to add one.
 */
import type { Layer } from '../../../types/layer';
import type { LegendEntry } from './legendModel';
import { getMarkerRhColor } from '../../liveLayers/providers/WindBarbPng';
import { VAR_CATEGORIES, VAR_SHAPE_SUFFIX, VAR_SPRITE_IDS } from '../../layers2/varSymbology';
import { AIRCRAFT_TYPES, affIconUrl } from '../../liveLayers/providers/affSymbology';

/**
 * Live Weather — the persistent map symbol is the station marker head, a filled
 * circle coloured by relative humidity (the temperature number sits on top, and
 * a wind barb shows wind). Swatch colours come straight from getMarkerRhColor.
 */
function liveWeatherLegend(): LegendEntry[] {
  const bands: { label: string; rh: number }[] = [
    { label: 'Humid (RH ≥ 70%)', rh: 80 },
    { label: 'Moderate (RH 51–69%)', rh: 60 },
    { label: 'Dry (RH 31–50%)', rh: 40 },
    { label: 'Very dry (RH < 31%)', rh: 20 },
  ];
  const NOTE_COLOR = '#374151';
  const rampEntries: LegendEntry[] = bands.map((b) => ({
    label: b.label,
    symbol: { kind: 'swatch', color: getMarkerRhColor(b.rh), shape: 'circle' },
  }));
  return [
    ...rampEntries,
    { label: 'Number = temperature (°C)', symbol: { kind: 'icon', iconName: 'temperature-half', color: NOTE_COLOR, size: 1 } },
    { label: 'Wind barb — full barb 10 km/h, half barb 5 km/h', symbol: { kind: 'icon', iconName: 'wind', color: NOTE_COLOR, size: 1 } },
  ];
}

/**
 * AFF (Live Aircraft) — the map draws a PNG per aircraft type + status. Show one
 * row per type (flying icon) plus a landed sample, using the real backend PNGs.
 */
function affLegend(): LegendEntry[] {
  const types: LegendEntry[] = AIRCRAFT_TYPES.map((t) => ({
    label: t.label,
    symbol: { kind: 'images', urls: [affIconUrl(t.code, 'flying'), affIconUrl(t.code, 'landed')], size: 0.5 },
  }));
  return [
    ...types,
    { label: 'Heartbeat', symbol: { kind: 'icon', iconName: 'heart', color: '#bd0000', size: 0.85 } },
  ];
}

/**
 * VAR (Values At Risk) — shape-by-category, state-by-color symbology.
 * One row per VAR category showing the baseline (no-state) sprite, plus
 * two state explainers (red border = flagged, blue droplet = sprinklered)
 * so users can decode any combined icon they see on the map.
 */
function varLegend(): LegendEntry[] {
  const shapeRows: LegendEntry[] = VAR_CATEGORIES.map((cat) => ({
    label: cat,
    symbol: {
      kind: 'image',
      url: VAR_SPRITE_IDS[`var-${VAR_SHAPE_SUFFIX[cat]}-0` as keyof typeof VAR_SPRITE_IDS],
    },
  }));
  // Use a different shape per state row so the state explainers don't
  // visually echo the Infrastructure type row right above them.
  const stateRows: LegendEntry[] = [
    {
      label: 'Red border = Red Flag active',
      symbol: { kind: 'image', url: VAR_SPRITE_IDS['var-di-f'] },
    },
    {
      label: 'Blue fill = Sprinkler deployed',
      symbol: { kind: 'image', url: VAR_SPRITE_IDS['var-pent-s'] },
    },
    {
      label: 'Blue + red border = both',
      symbol: { kind: 'image', url: VAR_SPRITE_IDS['var-tri-sf'] },
    },
  ];
  const stationRow: LegendEntry = {
    label: 'Weather Station',
    symbol: { kind: 'icon', iconName: 'cloud-rain', color: '#6b7280', size: 1.0 },
  };
  return [...shapeRows, ...stateRows, stationRow];
}

/** Custom legends keyed by a live layer's config.providerId. */
const BY_PROVIDER: Record<string, () => LegendEntry[]> = {
  'weather-stations': liveWeatherLegend,
  'aff': affLegend,
};

/** Custom legends keyed by layer type (for non-provider layers like VAR). */
const BY_TYPE: Record<string, () => LegendEntry[]> = {
  var: varLegend,
};

/** Optional header-title overrides, keyed by config.providerId. */
const TITLE_BY_PROVIDER: Record<string, string> = {
  aff: 'Live Aircraft/Landed Aircraft',
};

/** A header title override for a layer's legend, or null to use the layer name. */
export function getCustomLegendTitle(layer: Pick<Layer, 'type' | 'config'> | null | undefined): string | null {
  if (!layer) return null;
  const providerId = (layer.config as { providerId?: string } | null | undefined)?.providerId;
  return (providerId && TITLE_BY_PROVIDER[providerId]) || null;
}

/**
 * The provider-declared custom legend for a layer, or null when the layer has
 * no custom legend (it then falls back to the auto path). Resolves by
 * config.providerId first, then by layer type.
 */
export function getCustomLegend(layer: Pick<Layer, 'type' | 'config'> | null | undefined): LegendEntry[] | null {
  if (!layer) return null;
  const providerId = (layer.config as { providerId?: string } | null | undefined)?.providerId;
  if (providerId && BY_PROVIDER[providerId]) return BY_PROVIDER[providerId]();
  if (layer.type && BY_TYPE[layer.type]) return BY_TYPE[layer.type]();
  return null;
}
